import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBackwardStep,
  faAngleLeft,
  faAngleRight,
  faForwardStep,
} from '@fortawesome/free-solid-svg-icons';
import { PaginationProps } from '@helpers/Models';

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  /**
   * Navigates to the specified page if it is within the valid range.
   *
   * @param {number} page - The page number to navigate to.
   */
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  /**
   * Navigates to the next page if the current page is not the last page.
   */
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  /**
   * Navigates to the previous page if the current page is not the first page.
   */
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  /**
   * Navigates to the first page.
   */
  const goToFirstPage = () => onPageChange(1);

  /**
   * Navigates to the last page.
   */
  const goToLastPage = () => onPageChange(totalPages);

  /**
   * Handles the change event for items per page selection.
   *
   * This function:
   * - Parses the selected value as a number.
   * - Calls onItemsPerPageChange with the new count.
   * - Resets the current page to 1 by calling onPageChange.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event from the select element.
   */
  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newCount = Number(e.target.value);
    onItemsPerPageChange(newCount);
    onPageChange(1);
  };

  /**
   * Returns a list of visible page numbers for pagination, including ellipsis ('...') when needed.
   *
   * @param {number} currentPage - The currently active page.
   * @param {number} totalPages - The total number of pages.
   * @returns {(number | string)[]} An array of page numbers and ellipses to be displayed.
   *
   * How it works:
   * - If totalPages is 7 or less, return an array with all pages.
   * - If totalPages is greater than 7:
   *   - When currentPage is near the beginning (<= 4):
   *     - Display pages 1 through 5, followed by an ellipsis and the last page.
   *   - When currentPage is near the end (>= totalPages - 3):
   *     - Display the first page, an ellipsis, and the last 5 pages.
   *   - Otherwise (currentPage in the middle):
   *     - Display the first page, an ellipsis, the current page with one page before and after it,
   *       another ellipsis, and the last page.
   */
  const getPages = (
    currentPage: number,
    totalPages: number,
  ): (number | string)[] => {
    const visiblePages: (number | string)[] = [];

    // Handle case when total pages is small
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        visiblePages.push(i);
      }
      return visiblePages;
    }

    if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) {
        visiblePages.push(i);
      }
      visiblePages.push('...');
      visiblePages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      visiblePages.push(1);
      visiblePages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      visiblePages.push(1);
      visiblePages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        visiblePages.push(i);
      }
      visiblePages.push('...');
      visiblePages.push(totalPages);
    }
    return visiblePages;
  };

  return (
    <div className="flex justify-end items-center w-full">
      <div className="flex items-center gap-4 mb-6 mr-4 mt-5 px-3">
        <div className="flex items-center gap-3">
          <button
            onClick={goToFirstPage}
            disabled={currentPage === 1}
            type="button"
          >
            <FontAwesomeIcon
              icon={faBackwardStep}
              className="text-xs text-gray-800"
            />
          </button>
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            type="button"
          >
            <FontAwesomeIcon
              icon={faAngleLeft}
              className="text-xs text-gray-800"
            />
          </button>

          {getPages(currentPage, totalPages).map((page) =>
            typeof page === 'number' ? (
              <button
                key={`page-${page}`}
                onClick={() => goToPage(page)}
                type="button"
                className={`h-5 w-5 mt-1 text-xs rounded-full hover:bg-primary hover:text-white ${currentPage === page ? 'bg-primary text-white' : ''}`}
              >
                {page}
              </button>
            ) : (
              <span key={`ellipsis-${page}`} className="mx-1">
                {page}
              </span>
            ),
          )}

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            type="button"
          >
            <FontAwesomeIcon
              icon={faAngleRight}
              className="text-xs text-gray-800"
            />
          </button>
          <button
            onClick={goToLastPage}
            disabled={currentPage === totalPages}
            type="button"
          >
            <FontAwesomeIcon
              icon={faForwardStep}
              className="text-xs text-gray-800"
            />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 font-semibold">
            Lines per page
          </span>
          <select
            onChange={handleItemsPerPageChange}
            className="rounded-sm px-2 py-1 text-sm border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
            value={itemsPerPage}
          >
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
